import { useForm } from "@tanstack/react-form";
import styled from "styled-components";
import { useParams } from "react-router-dom";
import { Button } from "../../components/Button";
import { Form } from "../../components/Form";
import { FormGroup } from "../../components/FormGroup";
import { FormLabel } from "../../components/FormLabel";
import { FormActions } from "../../components/FormActions";
import { Input } from "../../components/Input";
import { MoneyInput } from "../../components/MoneyInput";
import { InfoBox } from "../../components/InfoBox";
import { useGetStoreQuery } from "../../query/storesQuery";
import { spacing } from "@styling/sizing";
import { fontSizes } from "@styling/fonts";
import colors from "@styling/colors";
import type { Money } from "@KassaroAB/shared/types/money";
import type { StoreId } from "@KassaroAB/shared/types/ids";
import { Currency } from "@KassaroAB/shared/types/enums/currency";
import type { RestCreateCreditNote, RestCreditNote } from "@KassaroAB/shared/types/creditNote";
import { useTranslation } from "react-i18next";
import { formatDate } from "../../utils/dateUtils";

const ReadOnlyField = styled.div`
  padding: ${spacing.x2}px;
  background-color: ${colors.backgroundDark};
  border-radius: 4px;
  font-size: ${fontSizes.regular}px;
  color: ${colors.textMuted};
  font-family: monospace;
`;

interface CreditNoteFormProps {
  creditNote?: RestCreditNote;
  onSubmit: (data: RestCreateCreditNote) => void;
  onCancel: () => void;
  isLoading?: boolean;
}

export const CreditNoteForm = ({
  creditNote,
  onSubmit,
  onCancel,
  isLoading,
}: CreditNoteFormProps) => {
  const { t } = useTranslation();
  const { storeId } = useParams<{ storeId: StoreId }>();
  const { data: store } = useGetStoreQuery(storeId);
  const storeCurrency = store?.currency || Currency.SEK;

  const form = useForm({
    defaultValues: {
      originalReceiptId: creditNote?.originalReceiptId ?? null,
      originalAmountAmount: creditNote?.originalAmount?.amount ? creditNote.originalAmount.amount / 100 : 0,
      remainingAmountAmount: creditNote?.remainingAmount?.amount ? creditNote.remainingAmount.amount / 100 : 0,
      useByDate: creditNote?.useByDate
        ? formatDate(creditNote.useByDate)
        : "",
      barcode: creditNote?.barcode ?? "",
    },
    onSubmit: ({ value }) => {
      const originalAmount: Money = {
        amount: value.originalAmountAmount * 100,
        currency: storeCurrency,
      };
      const remainingAmount: Money = {
        amount: value.remainingAmountAmount * 100,
        currency: storeCurrency,
      };
      onSubmit({
        storeId: storeId!,
        originalReceiptId: value.originalReceiptId,
        originalAmount,
        remainingAmount,
        useByDate: new Date(value.useByDate).toISOString(),
        barcode: value.barcode,
      });
    },
  });

  return (
    <Form
      onSubmit={(e) => {
        e.preventDefault();
        e.stopPropagation();
        form.handleSubmit();
      }}
    >
      <InfoBox variant="warning">
        {t("adminForms.warningNotBookkept")}
      </InfoBox>

      <form.Field name="originalAmountAmount">
        {(field) => (
          <MoneyInput
            label={t("creditNoteForm.originalAmount")}
            amount={field.state.value}
            currency={storeCurrency}
            onAmountChange={field.handleChange}
            onAmountBlur={field.handleBlur}
            required
          />
        )}
      </form.Field>

      <form.Field name="remainingAmountAmount">
        {(field) => (
          <MoneyInput
            label={t("creditNoteForm.remainingAmount")}
            amount={field.state.value}
            currency={storeCurrency}
            onAmountChange={field.handleChange}
            onAmountBlur={field.handleBlur}
            required
          />
        )}
      </form.Field>

      <form.Field name="useByDate">
        {(field) => (
          <FormGroup>
            <FormLabel>{t("creditNoteForm.useByDate")}</FormLabel>
            <Input
              type="date"
              value={field.state.value}
              onChange={(e) => field.handleChange(e.target.value)}
              onBlur={field.handleBlur}
              required
            />
          </FormGroup>
        )}
      </form.Field>

      <form.Field name="barcode">
        {(field) => (
          <FormGroup>
            <FormLabel>{t("creditNoteForm.barcode")}</FormLabel>
            <Input
              type="text"
              value={field.state.value}
              onChange={(e) => field.handleChange(e.target.value)}
              onBlur={field.handleBlur}
              required
            />
          </FormGroup>
        )}
      </form.Field>

      {creditNote && (
        <FormGroup>
          <FormLabel>{t("creditNoteForm.createdAt")}</FormLabel>
          <ReadOnlyField>
            {formatDate(creditNote.createdAt)}
          </ReadOnlyField>
        </FormGroup>
      )}

      <FormActions>
        <Button type="button" $color="secondary" onClick={onCancel} disabled={isLoading}>
          {t("common.cancel")}
        </Button>
        <Button type="submit" disabled={isLoading}>
          {isLoading ? t("common.saving") : creditNote ? t("common.update") : t("common.create")}
        </Button>
      </FormActions>
    </Form>
  );
};
